import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from '@floating-ui/react';
import {useIntl} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import styled, {useColors} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import type {XAlign, YAlign} from './floating/placement';
import {
  alignmentFromPlacement,
  placementFromPreferences,
} from './floating/placement';
import useToggle from './hooks/useToggle';
import IconButton from './IconButton';
import * as MenuIcons from './icons/menus';
import type {ElementConfig, LocalizedReactNode} from './types';

const TRANSITION_MS = 200;

// Matches the z-index `Layer` gives its portal, which is where this menu used
// to render.
const LAYER_Z_INDEX = 100;

const MenuWrapper = styled(
  'menu',
  (
    {
      $xAlign,
      $yAlign,
    }: {
      $xAlign: XAlign;
      $yAlign: YAlign;
    },
    designColors,
  ) => ({
    backgroundColor: designColors.contentBackground,
    borderColor: designColors.borderGrayNormal,
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderBottomWidth: '2px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderRadius: 4,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.125)',
    display: 'inline-flex',
    flexDirection: 'column',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxHeight: 300,
    minWidth: 200,
    outline: 'none',
    overflowY: 'auto',
    padding: '4px 0',
    transformOrigin: `${$xAlign === 'left' ? '20%' : '80%'} ${
      $yAlign === 'top' ? 'bottom' : 'top'
    }`,
  }),
);

const MenuOptionRow = styled(
  'button',
  (
    {
      $active,
      $destructive,
    }: {
      $active: boolean;
      $destructive: boolean;
    },
    designColors,
  ): CSS => ({
    WebkitAppearance: 'none',
    background: $active
      ? $destructive
        ? designColors.badgeRedBackground
        : designColors.accentBlueLight
      : 'none',
    border: 0,
    borderRadius: 3,
    color: $destructive ? designColors.badgeRedText : designColors.contentText,
    cursor: 'pointer',
    flex: '0 0',
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    margin: '0 4px',
    padding: 8,
    textAlign: 'left',
    whiteSpace: 'nowrap',

    // The highlight above tracks `useListNavigation`, which moves real DOM
    // focus, so the row is already the focused element. Keep an explicit ring
    // anyway: the fill alone would be conveying focus by color. `contentText` is
    // the one token that clears 3:1 against both the row fill and the menu
    // background in light and dark mode — `headingBlue` is only 2.06:1 on
    // `accentBlueLight` in the dark palette.
    ':focus-visible': {
      outline: `2px solid ${designColors.contentText}`,
      outlineOffset: '-2px',
    },
  }),
  {role: 'option'},
);

export type MenuOption<T> = {
  name: LocalizedReactNode;
  slug: T;
  destructive?: boolean;
};

type Props<T> = {
  children: React.ReactNode;
  id?: string;
  options: Array<MenuOption<T> | null | false>;
  open: boolean;
  onSelect: (slug: T) => void;
  onClose: () => void;
  toSelect?: T;
  wrapperStyle?: React.CSSProperties;
};

const ContextMenu = <T extends string | number>(props: Props<T>) => {
  const {
    children,
    id,
    onClose,
    onSelect,
    open,
    options,
    toSelect,
    wrapperStyle,
  } = props;

  // `options` carries `null`/`false` holes for conditionally-hidden rows. The
  // old keyboard handler counted them, so arrowing could land on a hole and
  // Enter would do nothing; indexing against the rendered rows fixes that.
  const items = React.useMemo(
    () => options.filter((option): option is MenuOption<T> => !!option),
    [options],
  );

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const selectedIndex = React.useMemo(() => {
    const found = items.findIndex(option => option.slug === toSelect);
    return found === -1 ? null : found;
  }, [items, toSelect]);

  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  // Option names are translated nodes rather than strings, so typeahead reads
  // the rendered text off the DOM instead of the option objects.
  const labelsRef = React.useRef<Array<string | null>>([]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        onClose();
      }
    },
    [onClose],
  );

  const {context, floatingStyles, placement, refs} = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement: placementFromPreferences('left', 'bottom'),
    middleware: [offset(0), flip({padding: 8}), shift({padding: 8})],
    // The listbox is the floating element, so `top`/`left` positioning keeps
    // `transform` free for the open/close scale transition.
    transform: false,
    whileElementsMounted: autoUpdate,
  });

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    selectedIndex,
    focusItemOnOpen: true,
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    onMatch: setActiveIndex,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'listbox'});

  const {getFloatingProps, getItemProps, getReferenceProps} = useInteractions([
    listNavigation,
    typeahead,
    dismiss,
    role,
  ]);

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {
    duration: TRANSITION_MS,
    initial: {opacity: 0, transform: 'scale(0.9)'},
    open: {opacity: 1, transform: 'scale(1)'},
  });

  const {xAlign, yAlign} = alignmentFromPlacement(placement);

  // `useRole` wants to stamp `role="combobox"` on the reference. Here the
  // reference is only a positioning anchor wrapped around whatever trigger the
  // caller passed — the trigger itself owns the interactive semantics — so the
  // role is dropped and the rest of the ARIA kept.
  const {role: _unusedReferenceRole, ...referenceProps} = getReferenceProps();

  return (
    <>
      <span
        ref={refs.setReference}
        style={{
          display: 'inline-flex',
          verticalAlign: 'middle',
          ...wrapperStyle,
        }}
        {...referenceProps}
      >
        {children}
      </span>
      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <MenuWrapper
              $xAlign={xAlign}
              $yAlign={yAlign}
              ref={refs.setFloating}
              // `__forceStyle` is an inline style rather than a generated
              // class: `autoUpdate` rewrites these coordinates on every scroll
              // and resize tick, which would otherwise mint a class per frame.
              __forceStyle={{
                ...floatingStyles,
                ...transitionStyles,
                zIndex: LAYER_Z_INDEX,
              }}
              {...getFloatingProps(id ? {id} : undefined)}
            >
              {items.map((option, i) => (
                <MenuOptionRow
                  $active={activeIndex === i}
                  $destructive={option.destructive || false}
                  aria-selected={selectedIndex === i}
                  id={id ? `${id}__${String(option.slug)}` : undefined}
                  key={String(option.slug)}
                  ref={(node: HTMLElement | null) => {
                    listRef.current[i] = node;
                    labelsRef.current[i] = node?.textContent ?? null;
                  }}
                  role="option"
                  tabIndex={activeIndex === i ? 0 : -1}
                  type="button"
                  {...getItemProps({
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelect(option.slug);
                      onClose();
                    },
                  })}
                >
                  {option.name}
                </MenuOptionRow>
              ))}
            </MenuWrapper>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default ContextMenu;

type MenuProps<T extends string | number> = {
  onSelect: Props<T>['onSelect'];
  options: Props<T>['options'];
  size?: ElementConfig<typeof IconButton>['size'];
  style?: CSS;
  // The button's accessible name. Pass one whenever several of these menus
  // appear together (say, one per table row) so each is distinguishable.
  title?: string;
  toSelect?: Props<T>['toSelect'];
};
const IconMenuFactory = function <T extends string | number>(
  Component: React.ComponentType<any>,
) {
  return (props: MenuProps<T>) => {
    const designColors = useColors(); // eslint-disable-line react-hooks/rules-of-hooks
    const {formatMessage} = useIntl(); // eslint-disable-line react-hooks/rules-of-hooks
    const [open, toggle] = useToggle(); // eslint-disable-line react-hooks/rules-of-hooks
    return (
      <ContextMenu<T>
        open={open}
        onClose={() => toggle(false)}
        onSelect={props.onSelect}
        options={props.options}
        toSelect={props.toSelect}
      >
        <IconButton
          aria-expanded={open}
          aria-haspopup="listbox"
          Component={Component}
          iconProps={{
            color:
              (props.style && props.style.color && String(props.style.color)) ||
              designColors.contentText,
            size: 20,
          }}
          onClick={toggle}
          size={props.size}
          style={props.style}
          title={
            props.title ??
            formatMessage({
              description: 'Label for toggling a menu button',
              defaultMessage: 'Toggle menu',
            })
          }
        />
      </ContextMenu>
    );
  };
};

export const KebabIconMenu = IconMenuFactory(MenuIcons.Kebab);
export const MeatballIconMenu = IconMenuFactory(MenuIcons.Meatball);
